import { Job } from '../api/jobs'

export const PROGRESS_ITEM_GREY = '#808080'

export const PROGRESS_QUEUED_SAVE_COLOR = '#FF6B00'

/** Slugs whose area fills are drawn at 50% opacity in LayerManager. */
const SEE_THROUGH_SLUG_PREFIXES = [
  'ceiling_',
  'clutter',
  'lighting',
  'wet_surface',
  'heat_index',
  'fumes',
  'air_quality',
]

/**
 * Whether a job layer actually paints opaque status on the 2D canvas (mirrors
 * LayerManager's meetsCompletedUnitsThreshold + wet-surface transparency).
 */
export function jobContributesVisibleLayer(
  job: Job,
  displayZeroValues = false,
): boolean {
  if (job.completed_units < 0) {
    return false
  }
  if (!displayZeroValues && job.completed_units === 0) {
    return false
  }
  const { slug } = job.type.job_type
  if (slug.startsWith('wet_surface') && job.completed_units === 1) {
    return false
  }
  return true
}

/**
 * Top painted layer for an item on the 2D progress canvas (ascending display_order;
 * last contributing job wins).
 */
export function getTopVisibleJobForItem(
  jobsForItem: Job[],
  displayZeroValues = false,
): Job | null {
  const drawable = jobsForItem.filter(j =>
    jobContributesVisibleLayer(j, displayZeroValues),
  )
  if (!drawable.length) {
    return null
  }
  drawable.sort(
    (a, b) => a.type.job_type.display_order - b.type.job_type.display_order,
  )
  return drawable[drawable.length - 1]
}

/**
 * Dominant color the user sees for one job layer on the 2D canvas.
 * Lines use stroke (display_color); areas use fill; points fill the disk with display_color.
 */
export function getPerceivedCanvasColor(
  job: Job,
  fallback = PROGRESS_ITEM_GREY,
): string {
  const {
    display_shape: displayShape,
    display_color: displayColor,
    secondary_display_color: secondaryDisplayColor,
  } = job.type.job_type

  if (displayShape === 'line') {
    return displayColor ?? fallback
  }
  if (displayShape === 'point') {
    return displayColor ?? fallback
  }

  const fillStyle = secondaryDisplayColor ?? displayColor ?? fallback
  const { slug } = job.type.job_type
  if (SEE_THROUGH_SLUG_PREFIXES.some(prefix => slug.startsWith(prefix))) {
    return fillStyle
  }
  return fillStyle
}

/** Stroke/fill colors matching LayerManager for a single job layer. */
export function getJobLayerColors(job: Job): {
  strokeStyle: string
  fillStyle: string
} {
  const {
    display_color: displayColor,
    secondary_display_color: secondaryDisplayColor,
    slug,
  } = job.type.job_type
  const strokeStyle = displayColor
  let fillStyle = secondaryDisplayColor ?? displayColor

  const isWetSurface = slug.startsWith('wet_surface')
  if (isWetSurface && job.completed_units === 1) {
    fillStyle = `${fillStyle}00`
  }

  return { strokeStyle, fillStyle }
}

/**
 * Color for one item's 3D mesh — matches the top visible 2D layer.
 */
export function colorForTopVisibleJob(
  job: Job | null,
  fallback = PROGRESS_ITEM_GREY,
): string {
  if (!job) {
    return fallback
  }
  return getPerceivedCanvasColor(job, fallback)
}

export function colorForItemJobs(
  jobsForItem: Job[],
  displayZeroValues = false,
  fallback = PROGRESS_ITEM_GREY,
): string {
  return colorForTopVisibleJob(
    getTopVisibleJobForItem(jobsForItem, displayZeroValues),
    fallback,
  )
}
